<template>
    <div class="whatsnew">
        <header class="whatsnew__header">
            <div class="whatsnew__header-icon">
                <mdicon name="star-four-points-outline" :size="28" />
            </div>
            <h1 class="whatsnew__title">{{ notes.title }}</h1>
            <p class="whatsnew__intro">{{ notes.intro }}</p>
        </header>

        <main class="whatsnew__content">
            <section v-for="section in notes.sections" :key="section.title" class="whatsnew__section">
                <div class="whatsnew__section-icon">
                    <mdicon :name="section.icon" :size="22" />
                </div>
                <div class="whatsnew__section-body">
                    <h2 class="whatsnew__section-title">{{ section.title }}</h2>
                    <p class="whatsnew__section-description">{{ section.description }}</p>
                    <ul class="whatsnew__highlights">
                        <li v-for="highlight in section.highlights" :key="highlight">
                            <mdicon name="check" :size="14" />
                            <span>{{ highlight }}</span>
                        </li>
                    </ul>
                </div>
            </section>
        </main>

        <div v-if="notes.link?.url" class="whatsnew__more-row">
            <!-- Echte href houdt de link toetsenbord-/screenreader-toegankelijk en
                 ondersteunt rechtsklik; de klik zelf gaat via de Capacitor Browser. -->
            <a
                class="whatsnew__more"
                data-testid="whatsnew-more"
                :href="notes.link.url"
                target="_blank"
                rel="noopener noreferrer"
                @click.prevent="openLink()"
            >
                <mdicon name="open-in-new" :size="14" />
                <span>{{ notes.link.label }}</span>
            </a>
        </div>

        <footer class="whatsnew__footer">
            <label class="whatsnew__checkbox">
                <input type="checkbox" v-model="dontShowAgain" />
                <span>Niet meer tonen</span>
            </label>
            <NcButton color="primary" data-testid="whatsnew-close" @click="close()">Aan de slag</NcButton>
        </footer>
    </div>
</template>

<script>
import { Browser } from '@capacitor/browser';
import { RELEASE_NOTES } from '@/data/releaseNotes';

export const WHATS_NEW_DISMISSED_KEY = 'whats_new_dismissed_id';

export default {
    name: 'whats-new-modal',
    data() {
        return {
            notes: RELEASE_NOTES,
            dontShowAgain: false,
        };
    },
    methods: {
        async openLink() {
            const url = this.notes.link?.url;
            if (!url) return;

            // Capacitor Browser werkt op native én web; val terug op window.open.
            try {
                await Browser.open({ url });
            } catch (e) {
                console.warn('WhatsNewModal: Browser.open faalde, val terug op window.open', e);
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        },
        close() {
            if (this.dontShowAgain) {
                localStorage.setItem(WHATS_NEW_DISMISSED_KEY, this.notes.id);
            }

            // Sluit specifiek déze modal via het host-element.
            const modal = this.$el.closest('ion-modal');
            if (modal) modal.dismiss();
        },
    },
};
</script>

<style>
/* Niet-scoped: ion-modal leeft buiten deze component */
ion-modal.whats-new-modal {
    --width: 600px;
    --max-width: 94vw;
    --height: 85vh;
    --max-height: 760px;
    --border-radius: var(--nc-radius-lg);
    --box-shadow: var(--nc-shadow-lg);
}
</style>

<style scoped>
.whatsnew {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--nc-bg);
    color: var(--nc-text);
    font-family: var(--nc-font-family);
}

.whatsnew__header {
    padding: var(--nc-spacing-lg) var(--nc-spacing-lg) var(--nc-spacing-md);
    text-align: center;
    border-bottom: 1px solid var(--nc-border);
    flex-shrink: 0;
}

.whatsnew__header-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto var(--nc-spacing-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--nc-radius-full);
    background: var(--nc-color-primary-light);
    color: var(--nc-color-primary);
}

.whatsnew__title {
    margin: 0;
    font-size: var(--nc-font-size-xl);
    font-weight: var(--nc-font-weight-bold);
}

.whatsnew__intro {
    margin: var(--nc-spacing-xs) auto 0;
    max-width: 420px;
    font-size: var(--nc-font-size-xs);
    color: var(--nc-text-muted);
}

.whatsnew__content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: var(--nc-spacing-md) var(--nc-spacing-lg);
}

.whatsnew__section {
    display: flex;
    gap: var(--nc-spacing-md);
    padding: var(--nc-spacing-md) 0;
}

.whatsnew__section + .whatsnew__section {
    border-top: 1px solid var(--nc-border);
}

.whatsnew__section-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--nc-radius-md);
    background: var(--nc-color-primary-light);
    color: var(--nc-color-primary);
}

.whatsnew__section-body {
    min-width: 0;
}

.whatsnew__section-title {
    margin: 0;
    font-size: var(--nc-font-size-sm);
    font-weight: var(--nc-font-weight-bold);
}

.whatsnew__section-description {
    margin: 2px 0 var(--nc-spacing-xs);
    font-size: var(--nc-font-size-xs);
    color: var(--nc-text-secondary);
}

.whatsnew__highlights {
    margin: 0;
    padding: 0;
    list-style: none;
}

.whatsnew__highlights li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: var(--nc-font-size-xs);
    color: var(--nc-text-muted);
    padding: 2px 0;
}

.whatsnew__highlights li .mdi {
    color: var(--nc-color-primary);
    flex-shrink: 0;
    margin-top: 2px;
}

.whatsnew__more-row {
    display: flex;
    justify-content: center;
    padding: var(--nc-spacing-sm) var(--nc-spacing-lg) 0;
    flex-shrink: 0;
}

.whatsnew__more {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--nc-font-size-xs);
    font-weight: var(--nc-font-weight-bold);
    color: var(--nc-color-primary);
    text-decoration: none;
    cursor: pointer;
}

.whatsnew__more:hover {
    text-decoration: underline;
}

.whatsnew__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--nc-spacing-md);
    padding: var(--nc-spacing-md) var(--nc-spacing-lg);
    padding-bottom: calc(var(--nc-spacing-md) + var(--nc-safe-area-bottom));
    border-top: 1px solid var(--nc-border);
    background: var(--nc-bg);
    flex-shrink: 0;
}

.whatsnew__checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--nc-font-size-xs);
    color: var(--nc-text-muted);
    cursor: pointer;
    user-select: none;
}

.whatsnew__checkbox input {
    width: 18px;
    height: 18px;
    accent-color: var(--nc-color-primary);
    cursor: pointer;
}
</style>
